import { useEffect, useRef } from "react";

const default_inactivity_timeout = 1000 * 60 * 30; // 30 minutes
const default_debuff_interval = 10 * 1000; // 10 seconds
const last_active_key = "last-active";

export const useInactivityTimeout = (
  onTimeout: () => Promise<void>,
  inactivityTimeout = default_inactivity_timeout,
) => {
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUpdate = useRef<number>(0);

  useEffect(() => {
    const lastActive = localStorage.getItem(last_active_key);
    if (!lastActive) {
      return;
    }
    const now = Date.now();
    lastUpdate.current = Number(lastActive);
    if (now - lastUpdate.current > default_inactivity_timeout) {
      return void onTimeout();
    }
    return stopInactivityTimer;
  }, []);

  const resetTimer = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => void onTimeout(), inactivityTimeout);
  };

  const updateLastActive = () => {
    const now = Date.now();
    if (now - lastUpdate.current > default_debuff_interval) {
      localStorage.setItem(last_active_key, now.toString());
      lastUpdate.current = now;
      resetTimer();
    }
  };

  const handleBrowserStorageEvent = (event: StorageEvent) => {
    if (event.key !== last_active_key) {
      return;
    }
    if (event.newValue) {
      updateLastActive();
    }
  };

  const startInactivityTimer = () => {
    const now = Date.now();
    localStorage.setItem(last_active_key, now.toString());
    window.addEventListener("mousemove", updateLastActive);
    window.addEventListener("keydown", updateLastActive);
    window.addEventListener("touchstart", updateLastActive);
    window.addEventListener("storage", handleBrowserStorageEvent);
    resetTimer();
  };

  const stopInactivityTimer = () => {
    window.removeEventListener("mousemove", updateLastActive);
    window.removeEventListener("keydown", updateLastActive);
    window.removeEventListener("touchstart", updateLastActive);
    window.removeEventListener("storage", handleBrowserStorageEvent);
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    localStorage.removeItem(last_active_key);
  };

  return { startInactivityTimer, stopInactivityTimer };
};
